import React, { useContext, useMemo } from 'react';
import { Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLock,
  faGlobe,
  faHouse,
  faLayerGroup,
  faGear,
  faArrowsRotate,
} from '@fortawesome/free-solid-svg-icons';
import { useAppDispatch, useAppSelector } from '../store';
import { setParams } from '../reducers/paramsReducer';
import { setShowImportGames, setShowManageGroups } from '../reducers/uiReducer';
import { getGames } from '../reducers/gamesReducer';
import { getFilters } from '../reducers/filtersReducer';
import { fetchGroups } from '../reducers/groupsReducer';
import { GroupKind, GroupModel, GroupVisibility } from '../types/GroupModel';
import AppContext from '../context/AppContext';

function iconFor(group: GroupModel) {
  if (group.Kind === GroupKind.Home) return faHouse;
  if (group.Visibility === GroupVisibility.Private) return faLock;
  return faGlobe;
}

// Switches which group the list is scoped to (My Space, Home, or any public collection): one
// meaningful set of games at a time.
export default function GamesGroupSwitcher(): React.ReactNode {
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useContext(AppContext);
  const { groups, loaded } = useAppSelector((state) => state.groupsReducer);
  const params = useAppSelector((state) => state.paramsReducer.params);
  const loading = useAppSelector((state) => state.gamesReducer.loading);
  const group = params.group;

  const home = useMemo(() => groups.find((g) => g.Kind === GroupKind.Home), [groups]);
  const active = useMemo(() => {
    const activeToken = group ?? home?.Token ?? null;
    return groups.find((g) => g.Token === activeToken) ?? home ?? null;
  }, [groups, group, home]);

  // Split the menu: the player's own groups vs. public ones. Home already sorts first.
  const mine = useMemo(() => groups.filter((g) => g.IsOwner), [groups]);
  const publicGroups = useMemo(() => groups.filter((g) => !g.IsOwner), [groups]);

  // Nothing to switch between until groups load, or for a signed-out visitor with only Home.
  if (!loaded || groups.length <= 1) {
    return null;
  }

  const select = (token: string) => {
    dispatch(setParams({ group: token }));
  };

  // Re-pull the current view: an iOS PWA has no reload button or pull-to-refresh. Refreshes the list,
  // facets and group counts, keeping the filters.
  const refresh = () => {
    dispatch(getGames(params));
    dispatch(getFilters(params));
    dispatch(fetchGroups());
  };

  const item = (group: GroupModel) => (
    <Dropdown.Item
      key={group.Id}
      className="cm-group-item"
      active={active?.Id === group.Id}
      onClick={() => select(group.Token)}
    >
      <FontAwesomeIcon icon={iconFor(group)} className="cm-group-item-icon" fixedWidth />
      <span className="cm-group-item-name">{group.Name}</span>
      <span className="cm-group-item-count">{group.GameCount}</span>
    </Dropdown.Item>
  );

  return (
    <div className="cm-group-switcher">
      <span className="cm-group-switcher-label">Games in</span>
      <Dropdown>
        <Dropdown.Toggle variant="light" size="sm" className="cm-group-switcher-toggle">
          <FontAwesomeIcon
            icon={active ? iconFor(active) : faLayerGroup}
            className="me-2"
            fixedWidth
          />
          {active?.Name ?? 'Home page'}
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {mine.length > 0 ? <Dropdown.Header>Yours</Dropdown.Header> : null}
          {mine.map(item)}
          {publicGroups.length > 0 ? (
            <>
              {mine.length > 0 ? <Dropdown.Divider /> : null}
              <Dropdown.Header>Public</Dropdown.Header>
              {publicGroups.map(item)}
            </>
          ) : null}
          {isAuthenticated ? (
            <>
              <Dropdown.Divider />
              <Dropdown.Item onClick={() => dispatch(setShowImportGames(true))}>
                <FontAwesomeIcon icon={faLayerGroup} className="me-2" fixedWidth />
                Add games…
              </Dropdown.Item>
              <Dropdown.Item onClick={() => dispatch(setShowManageGroups(true))}>
                <FontAwesomeIcon icon={faGear} className="me-2" fixedWidth />
                Manage groups…
              </Dropdown.Item>
            </>
          ) : null}
        </Dropdown.Menu>
      </Dropdown>
      <button
        type="button"
        className="cm-group-refresh"
        onClick={refresh}
        disabled={loading}
        title="Refresh games"
        aria-label="Refresh games"
      >
        <FontAwesomeIcon icon={faArrowsRotate} spin={loading} />
      </button>
    </div>
  );
}
